import {
  MARKS,
  BLOCKS,
  INLINES,
  Block,
  Inline,
} from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-react-renderer';
import type { ReactNode } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { TextLink } from '../../TextLink';
import {
  BoldWrapper,
  ItalicWrapper,
  UnderlineWrapper,
  EmbeddedWrapper,
} from './ContentfulRichText.styles';
import {
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
} from '../../../Text';
import { AdditionalInfo } from '../../AdditionalInfo';

export const createOptions = (): Options => {
  let headingCount = 0;

  return {
    renderMark: {
      [MARKS.BOLD]: (text: ReactNode) => <BoldWrapper>{text}</BoldWrapper>,
      [MARKS.ITALIC]: (text: ReactNode) => (
        <ItalicWrapper>{text}</ItalicWrapper>
      ),
      [MARKS.UNDERLINE]: (text: ReactNode) => (
        <UnderlineWrapper>{text}</UnderlineWrapper>
      ),
    },
    renderNode: {
      [INLINES.HYPERLINK]: ({ data }: Block | Inline, children: ReactNode) => {
        const isAnchorLink = data.uri?.includes('#content-headline-');

        return (
          <TextLink
            href={data.uri}
            {...(!isAnchorLink && {
              target: '_blank',
              rel: 'noopener noreferrer',
            })}
            variant="text"
          >
            {children}
          </TextLink>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const {
          data: {
            target: {
              sys: { contentType },
              fields,
            },
          },
        } = node;

        if (!fields) return null;

        if (contentType?.sys?.id === 'infoSnippet') {
          return <AdditionalInfo>{fields.text}</AdditionalInfo>;
        }

        if (contentType?.sys?.id === 'infoSnippetLong') {
          return (
            <AdditionalInfo>
              {documentToReactComponents(fields.text, createOptions())}
            </AdditionalInfo>
          );
        }

        const { embedded } = fields;
        return (
          <EmbeddedWrapper dangerouslySetInnerHTML={{ __html: embedded }} />
        );
      },
      [INLINES.EMBEDDED_ENTRY]: ({ data }) => {
        if (data.target.sys.contentType.sys.id === 'infoSnippet') {
          return <AdditionalInfo>{data.target.fields.text}</AdditionalInfo>;
        }
        return null;
      },
      [BLOCKS.HEADING_2]: (_: Block | Inline, children: ReactNode) => {
        headingCount += 1;
        const id = `content-headline-${headingCount}`;
        return (
          <Heading2 id={id} textColor="current">
            {children}
          </Heading2>
        );
      },
      [BLOCKS.HEADING_3]: (_: Block | Inline, children: ReactNode) => (
        <Heading3 textColor="current">{children}</Heading3>
      ),
      [BLOCKS.HEADING_4]: (_: Block | Inline, children: ReactNode) => (
        <Heading4 textColor="current">{children}</Heading4>
      ),
      [BLOCKS.HEADING_5]: (_: Block | Inline, children: ReactNode) => (
        <Heading5 textColor="current">{children}</Heading5>
      ),
      [BLOCKS.HEADING_6]: (_: Block | Inline, children: ReactNode) => (
        <Heading6 textColor="current">{children}</Heading6>
      ),
      [BLOCKS.OL_LIST]: (_: Block | Inline, children: ReactNode) => (
        <ol>{children}</ol>
      ),
      [BLOCKS.UL_LIST]: (_: Block | Inline, children: ReactNode) => (
        <ul>{children}</ul>
      ),
      [BLOCKS.LIST_ITEM]: (_: Block | Inline, children: ReactNode) => (
        <li>{children}</li>
      ),
      [BLOCKS.TABLE]: (_: Block | Inline, children: ReactNode) => (
        <table>
          <tbody>{children}</tbody>
        </table>
      ),
      [BLOCKS.TABLE_ROW]: (_: Block | Inline, children: ReactNode) => (
        <tr>{children}</tr>
      ),
      [BLOCKS.TABLE_CELL]: (_: Block | Inline, children: ReactNode) => (
        <td>{children}</td>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (_: Block | Inline, children: ReactNode) => (
        <th>{children}</th>
      ),
    },
  };
};
